import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  BeakerIcon,
  CalendarIcon,
  ClipboardListIcon,
} from '@heroicons/react/outline';
import { toIntlDateTimeFormat } from '@zedoc/date';
import TimelineEvent from './TimelineEvent';
import {
  getRecordStatusType,
  RECORD_TYPE_APPOINTMENT,
  RECORD_TYPE_DRUG,
  RECORD_TYPE_PROCEDURE,
} from '../../../EpisodesOfCare/helpers';
import {
  AppointmentDetails,
  MedicationDetails,
  ProcedureDetails,
} from '../../../../components/dialogs/EpisodeOfCareDetails';

const TimelineEventDomain = ({ projectId, event, clinicalEvents }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Get clinical event data by eventId from the clinicalEvents map
  const clinicalEventData = clinicalEvents[event.eventId] || {};
  const { domain, start, end, status, context } = clinicalEventData;

  const [visible, setVisible] = useState(false);
  const handleAppointmentClick = () => {
    setVisible(true);
  };
  const handleDialogClose = () => setVisible(false);

  const icon = useMemo(() => {
    switch (domain) {
      case RECORD_TYPE_APPOINTMENT:
        return <CalendarIcon className="h-4 w-4 flex-shrink-0" />;
      case RECORD_TYPE_DRUG:
        return <BeakerIcon className="h-4 w-4 flex-shrink-0" />;
      case RECORD_TYPE_PROCEDURE:
        return <ClipboardListIcon className="h-4 w-4 flex-shrink-0" />;
      default:
        return null;
    }
  }, [domain]);

  const title = useMemo(() => {
    switch (domain) {
      case RECORD_TYPE_APPOINTMENT:
        return t('appointments', { count: 1 });
      case RECORD_TYPE_DRUG:
        return t('medications', { count: 1 });
      case RECORD_TYPE_PROCEDURE:
        return t('procedures', { count: 1 });
      default:
        return null;
    }
  }, [domain, t]);

  const name = useMemo(() => {
    switch (domain) {
      case RECORD_TYPE_APPOINTMENT:
        return `${toIntlDateTimeFormat(language, start, {
          timeStyle: 'short',
        })} • ${(end - start) / 60000} min`;
      case RECORD_TYPE_DRUG:
        return context.name;
      case RECORD_TYPE_PROCEDURE:
        return context.type;
      default:
        return null;
    }
  }, [domain, context, start, end, language]);

  const details = useMemo(() => {
    switch (domain) {
      case RECORD_TYPE_APPOINTMENT:
        return (
          <AppointmentDetails
            appointment={clinicalEventData}
            visible={visible}
            onCancel={handleDialogClose}
          />
        );
      case RECORD_TYPE_DRUG:
        return (
          <MedicationDetails
            medication={clinicalEventData}
            visible={visible}
            onCancel={handleDialogClose}
          />
        );
      case RECORD_TYPE_PROCEDURE:
        return (
          <ProcedureDetails
            procedure={clinicalEventData}
            visible={visible}
            onCancel={handleDialogClose}
            isEditable
          />
        );
      default:
        return null;
    }
  }, [domain, clinicalEventData, visible]);

  return (
    <>
      <TimelineEvent
        type={getRecordStatusType(status)}
        icon={icon}
        title={title}
        name={name}
        date={start}
        projectId={projectId}
        onClick={handleAppointmentClick}
        isNonActionable
      />
      {details}
    </>
  );
};

TimelineEventDomain.propTypes = {
  projectId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  event: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  clinicalEvents: PropTypes.object,
};

TimelineEventDomain.defaultProps = {
  event: {},
  clinicalEvents: {},
};

export default TimelineEventDomain;
